import { Component, forwardRef, Input } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CropperDialogComponent } from "./cropper-dialog/cropper-dialog.component";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AlertService } from "../../service/alert.service";

@Component({
  selector: "app-resizable-upload",
  templateUrl: "./resizable-upload.component.html",
  styleUrls: ["./resizable-upload.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResizableUploadComponent),
      multi: true
    }
  ]
})
export class ResizableUploadComponent implements ControlValueAccessor {
  @Input() requireCropping = true;
  @Input() aspectRatio = 4 / 3;
  @Input() multiple = false;
  @Input() maxSize: number;
  @Input() accept = ".png,.jpg,.jpeg";
  files: File[] = [];
  disabled = false;

  constructor(private dialog: MatDialog, private alertService: AlertService) {}

  get formattedAccept(): string {
    return this.accept.toUpperCase().replace(/\./g, "").replace(/,/g, ", ");
  }

  onChange: any = () => {};

  onTouched: any = () => {};

  writeValue(files: any): void {
    this.files = files || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async onSelect(event) {
    this.handleInitialSelection();
    this.handleRejectedFiles(event.rejectedFiles);

    for (const file of event.addedFiles) {
      if (this.shouldValidateBeforeProcessing(file)) {
        continue;
      }

      const processedFile = await this.processFile(file, event);
      if (this.shouldValidateAfterProcessing(file, processedFile)) {
        continue;
      }

      if (processedFile) {
        this.addFile(processedFile);
      }
    }

    this.onChange(this.files);
    this.onTouched();
  }

  openCropperDialog(file: File, event: any): Promise<any> {
    const dialogRef = this.dialog.open(CropperDialogComponent, {
      data: { file, event, aspectRatio: this.aspectRatio },
      width: "600px",
      disableClose: true
    });

    return dialogRef.afterClosed().toPromise();
  }

  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);
    this.onChange(this.files);
    this.onTouched();
  }

  private shouldValidateBeforeProcessing(file: File): boolean {
    if (!this.requireCropping && this.isFileSizeInvalid(file)) {
      this.alertService.openSnackBar(
        `File ${file.name} exceeds the maximum size of ${this.maxSize}MB.`
      );
      return true;
    }
    return false;
  }

  private shouldValidateAfterProcessing(originalFile: File, processedFile: File | null): boolean {
    if (processedFile && this.requireCropping && this.isFileSizeInvalid(processedFile)) {
      this.alertService.openSnackBar(
        `Processed file ${originalFile.name} still exceeds the maximum size of ${this.maxSize}MB.`
      );
      return true;
    }
    return false;
  }

  private handleInitialSelection() {
    if (!this.multiple) {
      this.files = [];
    }
  }

  private handleRejectedFiles(rejectedFiles: any[]) {
    if (rejectedFiles.length > 0) {
      this.alertService.openSnackBar(
        `Unsupported file type. Accepted formats: ${this.formattedAccept}.`
      );
    }
  }

  private isFileSizeInvalid(file: File): boolean {
    return this.maxSize && file.size > this.maxSize * 1024 * 1024;
  }

  private async processFile(file: File, event: any): Promise<File | null> {
    if (!this.requireCropping) {
      return file;
    }

    const result = await this.openCropperDialog(file, event);
    if (!result) {
      return null;
    }

    const blob = this.dataURLtoBlob(result);
    return new File([blob], file.name, { type: file.type });
  }

  private addFile(file: File) {
    if (!this.multiple) {
      this.files = [file];
    } else {
      this.files.push(file);
    }
  }

  private dataURLtoBlob(dataurl: string) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }
}
